import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import TopupSalesPage from "../../../entities/SuperAdminPage/TopupSales";
import { planService, topupService } from "../../../api";
import LoaderHelper from "../../../utils/Loader/LoaderHelper";
import { useAlert } from "../../../context/AlertContext";

const EMPTY_FILTERS = {
  packId: "",
  country: "",
  status: "",
  startDate: null,
  endDate: null,
};

const TopupSales = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showAlert } = useAlert();
  const selectedCountry = String(location?.state?.country || "").trim();

  const [purchases, setPurchases] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  });
  const [totalRevenue, setTotalRevenue] = useState(null);
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  // The Control Centre enters with a country; it seeds the filter but stays
  // editable from here.
  const [filters, setFilters] = useState({
    ...EMPTY_FILTERS,
    country: selectedCountry,
  });
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const normalizeListResponse = (response) => {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response?.result)) return response.result;
    return [];
  };

  useEffect(() => {
    fetchPacks();
  }, []);

  useEffect(() => {
    fetchSales();
  }, [page, rowsPerPage, filters]);

  const fetchPacks = async () => {
    try {
      const result = await topupService.list();
      setPacks(normalizeListResponse(result));
    } catch (error) {
      // The pack filter is an optional convenience — a failure here must not
      // block the sales report itself.
      setPacks([]);
    }
  };

  const fetchSales = async () => {
    setLoading(true);
    setLoadError("");
    LoaderHelper.loaderStatus(true);
    try {
      const result = await topupService.sales({
        page,
        limit: rowsPerPage,
        packId: filters.packId || undefined,
        country: filters.country || undefined,
        status: filters.status || undefined,
        startDate: filters.startDate
          ? filters.startDate.format("YYYY-MM-DD")
          : undefined,
        endDate: filters.endDate ? filters.endDate.format("YYYY-MM-DD") : undefined,
      });

      if (result?.success === false) {
        setPurchases([]);
        setTotalRevenue(null);
        setPagination({ page: 1, limit: rowsPerPage, total: 0, pages: 0 });
        setLoadError(result?.message || "Could not load topup sales.");
        showAlert(result?.message || "Could not load topup sales.", "error");
        return;
      }

      // The payload may arrive at the top level or nested under `data`.
      const payload = result?.data && !Array.isArray(result.data) ? result.data : result;

      setPurchases(Array.isArray(payload?.purchases) ? payload.purchases : []);
      setPagination({
        page: payload?.pagination?.page ?? page,
        limit: payload?.pagination?.limit ?? rowsPerPage,
        total: payload?.pagination?.total ?? 0,
        pages: payload?.pagination?.pages ?? 0,
      });
      // Backend-authoritative. Never derived from the rows on screen.
      setTotalRevenue(payload?.totalRevenue ?? null);
    } catch (error) {
      setPurchases([]);
      setTotalRevenue(null);
      setPagination({ page: 1, limit: rowsPerPage, total: 0, pages: 0 });
      setLoadError(error?.message || "Error fetching topup sales");
      showAlert(error?.message || "Error fetching topup sales", "error");
    } finally {
      setLoading(false);
      LoaderHelper.loaderStatus(false);
    }
  };

  // Any filter change invalidates the current page window.
  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(1);
  };

  const handleResetFilters = () => {
    setFilters({ ...EMPTY_FILTERS });
    setPage(1);
  };

  const handleRowsPerPageChange = (value) => {
    setRowsPerPage(value);
    setPage(1);
  };

  const handleBack = () => {
    navigate("/dashboard/topupManagement", {
      state: selectedCountry ? { country: selectedCountry } : undefined,
    });
  };

  return (
    <TopupSalesPage
      purchases={purchases}
      pagination={pagination}
      totalRevenue={totalRevenue}
      packs={packs}
      filters={filters}
      loading={loading}
      loadError={loadError}
      page={page}
      rowsPerPage={rowsPerPage}
      setPage={setPage}
      onRowsPerPageChange={handleRowsPerPageChange}
      onFilterChange={handleFilterChange}
      onResetFilters={handleResetFilters}
      onBack={handleBack}
    />
  );
};

export default TopupSales;
